<template>
    <div class="register-container">
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading...</div>
      </div>
      <h2>Register New User</h2>
      <form @submit.prevent="registerUser">
        <div class="form-group">
          <label for="id">Employee ID:</label>
          <input
            type="text"
            :value="form.id || ''"
            placeholder="ID will be generated automatically"
            readonly
            class="readonly-input"
          />
        </div>
        <div class="form-group">
          <label for="username">Username:</label>
          <input type="text" v-model="form.username" required />
        </div>
        <div class="form-group">
          <label for="password">Password:</label>
          <input :type="showPassword ? 'text' : 'password'" v-model="form.password" required />
        </div>
        <div class="form-group">
          <label for="confirmPassword">Confirm Password:</label>
          <input :type="showPassword ? 'text' : 'password'" v-model="form.confirmPassword" required />
        </div>
        <div class="form-group show-password">
          <label></label>
          <div class="inline-control">
            <input type="checkbox" v-model="showPassword" id="showPassword" />
            <label for="showPassword">Show Password</label>
          </div>
        </div>
        <div class="form-group">
          <label for="email">Email:</label>
          <input type="email" v-model="form.email" required />
        </div>
        <div class="form-group">
          <label for="joinDate">Join Date:</label>
          <input type="date" v-model="form.joinDate" required />
        </div>
        <div class="form-group">
          <label for="department">Department:</label>
          <input type="text" v-model="form.department" required />
        </div>
        <div class="form-group">
          <label for="intime">In Time:</label>
          <input type="time" v-model="form.intime" required />
        </div>
        <div class="form-group">
          <label for="outtime">Out Time:</label>
          <input type="time" v-model="form.outtime" required />
        </div>
        <div class="form-group">
          <label for="systemAccessLevel">System Access Level:</label>
          <select v-model="form.systemAccessLevel" required>
            <option value="Staff">Staff</option>
            <option value="Management">Management</option>
            <option value="Executive">Executive</option>
            <option value="Administrator">Administrator</option>
          </select>
        </div>
        <div class="form-group">
          <label for="hasSupervisor">Has Supervisor:</label>
          <select id="hasSupervisor" v-model="form.hasSupervisor">
            <option :value="false">No</option>
            <option :value="true">Yes</option>
          </select>
        </div>

        <div class="form-group">
          <label for="supervisorName">Supervisor Name:</label>
          <select
            id="supervisorName"
            v-model="form.supervisorName"
            :required="form.hasSupervisor"
            :disabled="!form.hasSupervisor"
          >
            <option value="">Select Supervisor</option>
            <option
              v-for="employee in supervisorCandidates"
              :key="employee.Id"
              :value="employee.Username"
            >
              {{ employee.Username }}
            </option>
          </select>
        </div>
        <button type="submit">Register</button>
      </form>
    </div>
  </template>

<script>
import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  data () {
    return {
      form: {
        id: null,
        username: '',
        password: '',
        confirmPassword: '',
        email: '',
        joinDate: '',
        department: '',
        systemAccessLevel: 'Staff',
        intime: '',
        outtime: '',
        hasSupervisor: false,
        supervisorName: ''
      },
      employees: [],
      showPassword: false,
      loadingCount: 0
    }
  },
  computed: {
    isLoading () {
      return this.loadingCount > 0
    },
    supervisorCandidates () {
      return this.employees
        .filter(employee => employee.SystemAccessLevel !== 'Staff')
        .filter(employee => Number(employee.Resigned) !== 1)
        .sort((a, b) => a.Username.localeCompare(b.Username))
    }
  },
  methods: {
    startLoading () {
      this.loadingCount++
    },

    finishLoading () {
      this.loadingCount = Math.max(this.loadingCount - 1, 0)
    },

    withLoading (request) {
      this.startLoading()

      return request()
        .finally(() => {
          this.finishLoading()
        })
    },
    async registerUser () {
      if (!this.form.username || !this.form.username.trim()) {
        this.showMessage('Username is required to generate Employee ID.')
        return
      }

      if (this.form.password !== this.form.confirmPassword) {
        this.showMessage('Passwords do not match!')
        return
      }

      if (!this.validateTimeDifference()) {
        this.showMessage('The difference between the Out Time and the In Time must be 8:30: regular work hours for 7:30 and 1 hour break')
        this.form.intime = ''
        this.form.outtime = ''
        return
      }

      try {
        const generatedId = await this.generateUniqueEmployeeId()
        this.form.id = generatedId

        const payload = {
          ...this.form,
          id: generatedId,
          supervisorName: this.form.hasSupervisor ? this.form.supervisorName : null
        }

        await this.withLoading(() => axios.post(`${API_BASE_URL}/register`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }))

        this.showMessage(`User registered successfully!\nEmployee ID: ${generatedId}`)
      } catch (error) {
        if (error.response && error.response.data) {
          this.showMessage('Error registering user: ' + error.response.data)
        } else {
          this.showMessage('Error registering user: ' + error.message)
        }
      }
    },
    fetchEmployees () {
      this.withLoading(() => axios.get(`${API_BASE_URL}/employees`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          this.employees = response.data.sort((a, b) => a.Username.localeCompare(b.Username))
        })
        .catch(error => {
          console.error('Error fetching employee data:', error)
          this.showMessage('Error fetching supervisor list.')
        })
    },
    generateEmployeeIdCandidate () {
      const username = this.form.username.trim()
      const nameSeed = username
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0)

      const randomPart = Math.floor(Math.random() * 900000)
      return ((nameSeed + randomPart) % 900000) + 100000
    },

    async employeeIdExists (employeeId) {
      try {
        await this.withLoading(() => axios.get(`${API_BASE_URL}/employee/${employeeId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }))

        return true
      } catch (error) {
        if (error.response && error.response.status === 404) {
          return false
        }

        throw error
      }
    },
    async generateUniqueEmployeeId () {
      const maxAttempts = 30

      for (let i = 0; i < maxAttempts; i++) {
        const candidateId = this.generateEmployeeIdCandidate()
        const exists = await this.employeeIdExists(candidateId)

        if (!exists) {
          return candidateId
        }
      }

      throw new Error('Failed to generate a unique Employee ID. Please try again.')
    },
    validateTimeDifference () {
      const intime = this.form.intime
      const outtime = this.form.outtime

      if (!intime || !outtime) {
        return false
      }

      const [inHours, inMinutes] = intime.split(':').map(Number)
      const [outHours, outMinutes] = outtime.split(':').map(Number)

      const inTimeInMinutes = inHours * 60 + inMinutes
      const outTimeInMinutes = outHours * 60 + outMinutes

      const differenceInMinutes = outTimeInMinutes - inTimeInMinutes

      return differenceInMinutes === 510 // 8 hours 30 minutes = 510 minutes
    },
    showMessage (message) {
      alert(message)
    }
  },
  watch: {
    'form.hasSupervisor' (newVal) {
      if (!newVal) {
        this.form.supervisorName = ''
      }
    }
  },
  created () {
    this.fetchEmployees()
  }
}

</script>

  <style scoped>
  .register-container {
    max-width: 620px;
    margin: auto;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  h2 {
    text-align: center;
  }

  form {
    width: 100%;
  }

  .form-group {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
  }

  .form-group label {
  width: 190px;
  margin-right: 10px;
  text-align: right;
  flex-shrink: 0;
  }

  .form-group input[type="text"],
  .form-group input[type="number"],
  .form-group input[type="password"],
  .form-group input[type="email"],
  .form-group input[type="date"],
  .form-group input[type="time"],
  .form-group select {
    width: 240px;
    flex: 0 0 240px;
    padding: 8px;
    box-sizing: border-box;
  }

  .inline-control {
    width: 240px;
    flex: 0 0 240px;
    display: flex;
    align-items: center;
  }

  .inline-control input[type="checkbox"] {
    margin-left: 0;
    margin-right: 6px;
    width: 14px;
    flex: 0 0 14px;
  }

  .inline-control label {
    width: auto;
    margin: 0;
    text-align: left;
  }

  button {
    width: 100%;
    padding: 10px;
    background-color: #007bff;
    border: none;
    color: white;
    cursor: pointer;
  }

  button:hover {
    background-color: #0056b3;
  }

  .readonly-input {
  background-color: #f2f2f2;
  color: #555;
  cursor: not-allowed;
  }

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.65);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #ccc;
  border-top: 5px solid #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin-top: 12px;
  font-weight: bold;
  font-size: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
  </style>
